/** Soft AT refresh timing for browser demos (no WebSocket reconnect). */

/** Prefer refresh at least this many seconds before expiry. */
export const TOKEN_REFRESH_MARGIN_SEC = 300;

/** Do not schedule soft refresh more often than this. */
export const TOKEN_REFRESH_MIN_SEC = 600;

/**
 * Delay until soft refresh from `expires_in_sec`.
 *
 * Uses ~85% of TTL, not later than margin before expiry, not sooner than min interval.
 *
 * @param {number} expiresInSec
 * @returns {number}
 */
export function computeTokenRefreshDelaySec(expiresInSec) {
  const ttlSec = Math.max(60, Number(expiresInSec) || 3600);
  let refreshInSec = Math.floor(ttlSec * 0.85);
  refreshInSec = Math.min(refreshInSec, Math.max(60, ttlSec - TOKEN_REFRESH_MARGIN_SEC));
  return Math.max(TOKEN_REFRESH_MIN_SEC, refreshInSec);
}

/**
 * Schedules recurring soft AT refresh via demo-server (MT stays on server).
 *
 * @param {object} options
 * @param {number} options.expiresInSec
 * @param {() => Promise<{ expires_in_sec: number, token?: string }>} options.refresh
 * @param {(result: { expires_in_sec: number, token?: string }) => void} [options.onSoftOk]
 * @param {(error: Error) => void | Promise<void>} [options.onError]
 * @returns {{ clear: () => void }}
 */
export function scheduleSoftTokenRefresh(options) {
  let timerId = null;
  let stopped = false;

  const arm = (expiresInSec) => {
    if (stopped) {
      return;
    }
    if (timerId !== null) {
      clearTimeout(timerId);
      timerId = null;
    }
    const delaySec = computeTokenRefreshDelaySec(expiresInSec);
    console.info(
      `[token] soft refresh in ${delaySec}s (~${Math.round(delaySec / 60)} min), TTL ${expiresInSec}s`
    );
    timerId = setTimeout(async () => {
      timerId = null;
      if (stopped) {
        return;
      }
      try {
        const result = await options.refresh();
        options.onSoftOk?.(result);
        arm(result.expires_in_sec);
      } catch (error) {
        await options.onError?.(error instanceof Error ? error : new Error(String(error)));
      }
    }, delaySec * 1000);
  };

  arm(options.expiresInSec);

  return {
    clear() {
      stopped = true;
      if (timerId !== null) {
        clearTimeout(timerId);
        timerId = null;
      }
    }
  };
}
